import { useCallback, useState } from 'react';

import { useToastContext } from '@rtkwlf/fenrir-react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

import { TOAST_DURATION } from 'ae-constants/toast';

export type DownloadType = {
  error: unknown | null;
  download: () => void;
  setError: (error: unknown | null) => void;
};

// This hook is used to create a blob based on the text data and allows to download the file.
export const useDownload = (fileName: string, text?: string): DownloadType => {
  const [error, setError] = useState<unknown>(null);
  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const download = useCallback(() => {
    setError(null);

    try {
      if (text) {
        const downloadUrl = window.URL.createObjectURL(new Blob([text]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      setError(err);
      Sentry.captureException(err);
      addToast({
        appearance: 'danger',
        duration: TOAST_DURATION,
        message: t('detailsPage:exportErrorMessage'),
        title: t('detailsPage:exportErrorTitle'),
      });
    }
  }, [addToast, fileName, t, text]);

  return { download, error, setError };
};
